/**
 * 代理模块接口
 * @description 代理信息、价格政策、站点限制等代理相关接口
 */

import axios from "@/utils/request";
import Api from "../api";
import * as Common from "../common.type";
import * as Request from "../request.type";
import * as ResponseData from "../response.type";
import { orgRangeTypeInfo } from "@/utils/enumerations";

/**
 * 价格政策代理列表
 */
export const Http_postPricePolicyAgencyList = async (
  params: Request.PricePolicyAgencyListParams
) => {
  return await axios.post(Api.POST_PRICE_POLICY_AGENCY_LIST, params);
};

/**
 * 代理同步信息
 * @returns 获取信息
 */
export const Http_postAgencyAsync = async (
  /** 详情ID */
  agencyId: number
) => {
  return await axios.post<ResponseData.AgencyAsyncData>(Api.POST_AGENCY_SYNC, {
    agencyId,
  });
};

/**
 * 代理切换信息
 * @returns 获取信息
 */
export const Http_postAgencySwitch = async (
  /** 详情ID */
  agencyId: number
) => {
  return await axios.post<ResponseData.AgencySwitchData>(
    Api.POST_AGENCY_SWITCH,
    {
      agencyId,
    }
  );
};

/**
 * 根据统计编码代理id查询成员信息接口
 */
export const Http_findSubAgencyWithFinance = async (
  /** 代理ID */
  id: number
) => {
  return await axios.get<ResponseData.AgencyOption[]>(
    Api.GET_Find_Sub_Agency_With_Finance_RefId,
    {
      params: {
        id,
      },
    }
  );
};

/**
 * 查询代理不可售站点列表
 */
export const Http_postSiteRestrictionsList = async (
  params: {
    agencyId?: number;
    /** 代理编号 */
    agencyCode?: number;
    /** 状态 1-启用，0-停用 */
    status?: 0 | 1;
    /** 站点ID数组 */
    stationIds?: number[];
    /** 站点名称 */
    stationName?: string;
  } & Common.BasePaginationParams
) => {
  return await axios.post<
    Common.PaginationResponse<ResponseData.SiteRestrictionsListItem[]>
  >(Api.POST_SITE_RESTRICTIONS_LIST, params);
};

/**
 * 设置代理不可售站点启用禁用
 */
export const Http_postSiteRestrictionsStatus = async (params: {
  status: 0 | 1;
  id: number;
}) => {
  return await axios.post(Api.POST_SITE_RESTRICTIONS_STATUS, params);
};

/**
 * 添加/修改代理不可售站点
 */
export const Http_postSiteRestrictionsAddOrUpdate = async (
  params: Request.SiteRestrictionsAddOrUpdateParams
) => {
  return await axios.post(Api.POST_SITE_RESTRICTIONS_ADD_OR_UPDATE, params);
};

/**
 * 获取代理不可售站点详情
 * @param id 详情ID
 */
export const Http_getSiteRestrictionsDetail = async (id: string) => {
  return await axios.post<
    {
      agencyList: {
        name: string;
        id: number;
        code: number;
        type: keyof typeof orgRangeTypeInfo;
      }[];
    } & ResponseData.SiteRestrictionsListItem
  >(Api.GET_SITE_RESTRICTIONS_DETAIL, {
    id,
  });
};

/**
 * 获取城市ID
 */
export const Http_getUserSerialNoList = async (
  /** 订单ID */
  cityCode: string
) => {
  return await axios.get<ResponseData.UserSerialNoItem[]>(
    Api.GET_AGENT_UserSerialNoList,
    {
      params: {
        cityCode,
      },
    }
  );
};

/**
 * 获取代理价格政策
 * @param id 代理ID
 * @returns 代理价格政策数据
 */
export const Http_getAgentSalePricePolicy = async (id: string) => {
  return await axios.get<ResponseData.AgentSalePricePolicyData>(
    Api.GET_AGENCY_SALE_PRICE_POLICY,
    {
      params: { id },
    }
  );
};

/**
 * 查询营销价列表
 * @param params 查询参数
 * @returns 营销价列表数据
 */
export const Http_postPriceMarketList = async (
  params: Request.GetPriceMarketListParams
) => {
  return await axios.post<ResponseData.PriceMarketListData>(
    Api.GET_PRICE_MARKET_LIST,
    params
  );
};

/**
 * 修改代理用户姓名
 * @param params 修改姓名参数
 * @returns 修改结果
 */
export const Http_postModifyAgencyUserName = async (
  params: Request.ModifyAgencyUserNameParams
) => {
  return await axios.post(Api.POST_MODIFY_AGENCY_USER_NAME, params);
};

/**
 * 修改代理编码
 * @param params 修改代理编码参数
 * @returns 修改结果
 */
export const Http_postModifyAgencyCode = async (
  params: Request.ModifyAgencyCodeParams
) => {
  return await axios.post(Api.POST_MODIFY_AGENCY_CODE, params);
};

/**
 * 查询代理用户详情
 * @param id 用户ID
 * @returns 用户详情数据
 */
export const Http_getAgencyUserById = async (id: string) => {
  return await axios.get<ResponseData.AgencyUserDetailData>(
    `${Api.GET_AGENCY_USER_BY_ID}?id=${id}`
  );
};

/**
 * 修改用户密码
 * @param params 修改密码参数
 * @returns 修改结果
 */
export const Http_postModifyPassword = async (
  params: Request.ModifyPasswordParams
) => {
  return await axios.post(Api.POST_MODIFY_PASSWORD, params);
};

/**
 * 修改代理用户信息
 * @param params 修改用户信息参数
 * @returns 修改结果
 */
export const Http_postModifyAgencyUser = async (
  params: Request.ModifyAgencyUserParams
) => {
  return await axios.post(Api.POST_MODIFY_AGENCY_USER, params);
};

/**
 * 启用或禁用用户
 * @param params 启用或禁用用户参数
 * @returns 修改结果
 */
export const Http_getEnableOrDisableUser = async (
  params: Request.EnableOrDisableUserParams
) => {
  return await axios.get(Api.GET_ENABLE_OR_DISABLE_USER, { params });
};

/**
 * 获取所有角色
 * @returns 角色列表
 */
export const Http_getAllRoles = async () => {
  return await axios.get<{ data: ResponseData.RoleItem[] }>(Api.GET_ALL_ROLES);
};

/**
 * 代理编号匹配查询
 * @param params 查询参数
 * @returns 代理编号匹配结果
 */
export const Http_getAgencyMatchCode = async (
  params?: Request.AgencyMatchCodeParams
) => {
  return await axios.get<ResponseData.AgencyMatchCodeData>(
    Api.GET_AGENCY_MATCH_CODE,
    { params }
  );
};
